import * as Events from '../events'

export type EventTrackingMethodName = Exclude<keyof EventTracker, '__EVENT_TRACKER__'>

/**
 * The EventTracker is essentially an interface, but is a concrete class so we can do some
 *   reflection over it to handle events generically (see the comment on the static method
 *   `getEventTrackingMethodNames` for more context).
 */
export class EventTracker {
  /**
   * This method allows for some limited runtime "reflection" of the event tracking methods.
   *   Generic trackers that do not need to manually implement every event tracking method
   *   can instead programmatically implement every method using this helper.
   *
   * See the `GenericEventTracker` as an example.
   *
   * Note: Other "generic" trackers should extend the `GenericEventTracker` rather than
   *   use this helper directly.
   */
  public static getEventTrackingMethodNames() {
    return Object.getOwnPropertyNames(EventTracker.prototype).filter((propertyName) => {
      return propertyName !== 'constructor' && propertyName !== '__EVENT_TRACKER__'
    }) as Array<EventTrackingMethodName>
  }

  /**
   * HACK: This field is used to determine whether an object is an EventTracker. This is
   *   only necessary for the stubbed Cypress tracker in the `e2e-testing` repo. Because
   *   Cypress runs in a separate environment from the application being tested, checking
   *   for prototype equality with "instanceof" is not possible.
   */
  public __EVENT_TRACKER__ = true

  public trackArticleEndViewed(_event: Events.ArticleEndViewedEvent) {}
  public trackArticleEntryPointClicked(_event: Events.ArticleEntryPointClickedEvent) {}
  public trackArticleHubEntryPointClicked(_event: Events.ArticleHubEntryPointClickedEvent) {}
  public trackArticleReadNextClicked(_event: Events.ArticleReadNextClickedEvent) {}
  public trackArticleSocialShareClicked(_event: Events.ArticleSocialShareClickedEvent) {}
  public trackArticleViewed(_event: Events.ArticleViewedEvent) {}
  public trackBrandCategoryModalDismissed(_event: Events.BrandCategoryModalDismissedEvent) {}
  public trackBrowserNotificationPermissionDenied(
    _event: Events.BrowserNotificationPermissionDeniedEvent
  ) {}
  public trackBrowserNotificationPermissionGranted(
    _event: Events.BrowserNotificationPermissionGrantedEvent
  ) {}
  public trackBrowserNotificationPermissionRequested(
    _event: Events.BrowserNotificationPermissionRequestedEvent
  ) {}
  public trackCartAccepted(_event: Events.CartAcceptedEvent) {}
  public trackChartersRedirectVisited(_event: Events.ChartersRedirectVisitedEvent) {}
  public trackCheckoutStarted(_event: Events.CheckoutStartedEvent) {}
  public trackCheckoutStepCompleted(_event: Events.CheckoutStepCompletedEvent) {}
  public trackCheckoutStepViewed(_event: Events.CheckoutStepViewedEvent) {}
  public trackClientSessionReset(_event: Events.ClientSessionResetEvent) {}
  public trackConsumerReferralLinkShareButtonClicked(
    _event: Events.ConsumerReferralLinkShareButtonClickedEvent
  ) {}
  public trackConsumerVisitedFromReferralLink(
    _event: Events.ConsumerVisitedFromReferralLinkEvent
  ) {}
  public trackContactCollectionCardSubmitted(_event: Events.ContactCollectionCardSubmittedEvent) {}
  public trackConversationStarterClicked(_event: Events.ConversationStarterClickedEvent) {}
  public trackCuratedItemRemoved(_event: Events.CuratedItemRemovedEvent) {}
  public trackCuratedListExpertNoteHelpful(_event: Events.CuratedListExpertNoteHelpfulEvent) {}
  public trackCuratedListExpertNoteNotHelpful(
    _event: Events.CuratedListExpertNoteNotHelpfulEvent
  ) {}
  public trackCuratedListViewed(_event: Events.CuratedListViewedEvent) {}
  public trackCuratedStatusBarViewCurationClicked(
    _event: Events.CuratedStatusBarViewCurationClickedEvent
  ) {}
  public trackDesktopFixedBarClicked(_event: Events.DesktopFixedBarClickedEvent) {}
  public trackEngagementChannelDismissed(_event: Events.EngagementChannelDismissedEvent) {}
  public trackEngagementChannelSubmitted(_event: Events.EngagementChannelSubmittedEvent) {}
  public trackEngagementChannelViewed(_event: Events.EngagementChannelViewedEvent) {}
  public trackEngagementModalDismissed(_event: Events.EngagementModalDismissedEvent) {}
  public trackEngagementModalSubmitted(_event: Events.EngagementModalSubmittedEvent) {}
  public trackExperimentViewed(_event: Events.ExperimentViewedEvent) {}
  public trackExpertAddedProductToCart(_event: Events.ExpertAddedProductToCartEvent) {}
  public trackExpertAddedRecommendedItem(_event: Events.ExpertAddedRecommendedItemEvent) {}
  public trackExpertApplicationSubmitted(_event: Events.ExpertApplicationSubmittedEvent) {}
  public trackExpertAttemptedToClaimLeadEscalation(
    _event: Events.ExpertAttemptedToClaimLeadEscalationEvent
  ) {}
  public trackExpertCalloutChatClicked(_event: Events.ExpertCalloutChatClickedEvent) {}
  public trackExpertCreatedExperienceCuration(
    _event: Events.ExpertCreatedExperienceCurationEvent
  ) {}
  public trackExpertCuratedItemAdded(_event: Events.ExpertCuratedItemAddedEvent) {}
  public trackExpertCuratedItemVariationSelected(
    _event: Events.ExpertCuratedItemVariationSelectedEvent
  ) {}
  public trackExpertCuratedOfferCtaClicked(_event: Events.ExpertCuratedOfferCtaClickedEvent) {}
  public trackExpertDismissedSuggestedAction(_event: Events.ExpertDismissedSuggestedActionEvent) {}
  public trackExpertLeadEscalationNotificationFiltered(
    _event: Events.ExpertLeadEscalationNotificationFilteredEvent
  ) {}
  public trackExpertNoteHelpful(_event: Events.ExpertNoteHelpfulEvent) {}
  public trackExpertNoteNotHelpful(_event: Events.ExpertNoteNotHelpfulEvent) {}
  public trackExpertProfileConversationStarted(
    _event: Events.ExpertProfileConversationStartedEvent
  ) {}
  public trackExpertProfileSellableClick(_event: Events.ExpertProfileSellableClickEvent) {}
  public trackExpertProfileCategoryTableChanged(
    _event: Events.ExpertProfileCategoryTableChangedEvent
  ) {}
  public trackExpertRatedShiftDifficulty(_event: Events.ExpertRatedShiftDifficultyEvent) {}
  public trackExpertRatingFlowCompleted(_event: Events.ExpertRatingFlowCompletedEvent) {}
  public trackExpertRatingFlowStarted(_event: Events.ExpertRatingFlowStartedEvent) {}
  public trackExpertRemovedRecommendedItem(_event: Events.ExpertRemovedRecommendedItemEvent) {}
  public trackExpertStarredLead(_event: Events.ExpertStarredLeadEvent) {}
  public trackExpertUnstarredLead(_event: Events.ExpertUnstarredLeadEvent) {}
  public trackFeedbackSubmitted(_event: Events.FeedbackSubmittedEvent) {}
  public trackFullPageTakeoverModalDismissed(_event: Events.FullPageTakeoverModalDismissedEvent) {}
  public trackFullPageTakeoverModalSubmitted(_event: Events.FullPageTakeoverModalSubmittedEvent) {}
  public trackGuestCheckoutStarted(_event: Events.GuestCheckoutStartedEvent) {}
  public trackHiringPageSubmitted(_event: Events.HiringPageSubmittedEvent) {}
  public trackHiringPageViewed(_event: Events.HiringPageViewedEvent) {}
  public trackItineraryPageViewed(_event: Events.ItineraryPageViewedEvent) {}
  public trackLandingPageSubmitted(_event: Events.LandingPageSubmittedEvent) {}
  public trackLandingPageViewed(_event: Events.LandingPageViewedEvent) {}
  public trackLeadViewed(_event: Events.LeadViewedEvent) {}

  public trackMobileFixedBarClicked(_event: Events.MobileFixedBarClickedEvent) {}
  public trackNoConversationsRequestModalTriggered(
    _event: Events.NoConversationsRequestModalTriggeredEvent
  ) {}
  public trackOrderCompleted(_event: Events.OrderCompletedEvent) {}
  public trackPageInteracted(_event: Events.PageInteractedEvent) {}
  public trackPageViewed(_event: Events.PageViewedEvent) {}
  public trackPathClientFlowCreated(_event: Events.PathClientFlowCreatedEvent) {}
  public trackPathClientFlowCompleted(_event: Events.PathClientFlowCompletedEvent) {}
  public trackPathClientFlowEntityCreated(_event: Events.PathClientFlowEntityCreatedEvent) {}
  public trackPathClientFlowNodeSubmitted(_event: Events.PathClientFlowNodeSubmittedEvent) {}
  public trackPathClientFlowNodeViewed(_event: Events.PathClientFlowNodeViewedEvent) {}
  public trackPathExited(_event: Events.PathExitedEvent) {}
  public trackPathFlowCreated(_event: Events.PathFlowCreatedEvent) {}
  public trackPathPageSubmitted(_event: Events.PathPageSubmittedEvent) {}
  public trackPathPageViewed(_event: Events.PathPageViewedEvent) {}
  public trackPathQuestionSubmitted(_event: Events.PathQuestionSubmittedEvent) {}
  public trackPathQuestionViewed(_event: Events.PathQuestionViewedEvent) {}
  public trackPaymentCancelled(_event: Events.PaymentCancelledEvent) {}
  public trackPaymentFailed(_event: Events.PaymentFailedEvent) {}
  public trackPaymentInfoEntered(_event: Events.PaymentInfoEnteredEvent) {}
  public trackPhoneVerificationCodeIssued(_event: Events.PhoneVerificationCodeIssuedEvent) {}
  public trackPhoneVerificationCodeConfirmed(_event: Events.PhoneVerificationCodeConfirmedEvent) {}
  public trackPhoneVerificationCodeErrored(_event: Events.PhoneVerificationCodeErroredEvent) {}
  public trackPhoneVerificationContactUpdated(
    _event: Events.PhoneVerificationContactUpdatedEvent
  ) {}
  public trackPriceComparisonsChatWithExpert(_event: Events.PriceComparisonsChatWithExpertEvent) {}
  public trackPriceComparisonsCollapsed(_event: Events.PriceComparisonsCollapsedEvent) {}
  public trackPriceComparisonsExpanded(_event: Events.PriceComparisonsExpandedEvent) {}
  public trackPriceComparisonsViewed(_event: Events.PriceComparisonsViewedEvent) {}
  public trackProductAddClicked(_event: Events.ProductAddClickedEvent) {}
  public trackProductAdded(_event: Events.ProductAddedEvent) {}
  public trackProductAddedToWishlist(_event: Events.ProductAddedToWishlistEvent) {}
  public trackProductBuyOffsiteClicked(_event: Events.ProductBuyOffsiteClickedEvent) {}
  public trackProductClicked(_event: Events.ProductClickedEvent) {}
  public trackProductDetailsExpanded(_event: Events.ProductDetailsExpandedEvent) {}
  public trackProductDetailsCollapsed(_event: Events.ProductDetailsCollapsedEvent) {}
  public trackProductRated(_event: Events.ProductRatedEvent) {}
  public trackProductRemoved(_event: Events.ProductRemovedEvent) {}
  public trackProductRemovedFromWishlist(_event: Events.ProductRemovedFromWishlistEvent) {}
  public trackProductsSearched(_event: Events.ProductsSearchedEvent) {}
  public trackProductsSearchResultsViewed(_event: Events.ProductsSearchResultsViewedEvent) {}
  public trackProductSpecsExpanded(_event: Events.ProductSpecsExpandedEvent) {}
  public trackProductSpecsCollapsed(_event: Events.ProductSpecsCollapsedEvent) {}
  public trackProductViewed(_event: Events.ProductViewedEvent) {}
  public trackProductViewEnded(_event: Events.ProductViewEndedEvent) {}
  public trackProfileSetupPageSubmitted(_event: Events.ProfileSetupPageSubmittedEvent) {}
  public trackProfileSetupPageViewed(_event: Events.ProfileSetupPageViewedEvent) {}
  public trackPushNotificationPromptAccepted(_event: Events.PushNotificationPromptAcceptedEvent) {}
  public trackPushNotificationPromptDeclined(_event: Events.PushNotificationPromptDeclinedEvent) {}
  public trackPushNotificationPromptDisplayed(
    _event: Events.PushNotificationPromptDisplayedEvent
  ) {}
  public trackQuickReplyUsed(_event: Events.QuickReplyUsedEvent) {}
  public trackReferralPageEntryPointViewed(_event: Events.ReferralPageEntryPointViewedEvent) {}
  public trackReferralPageViewed(_event: Events.ReferralPageViewedEvent) {}
  public trackRequestSubmitted(_event: Events.RequestSubmittedEvent) {}
  public trackSessionAssociated(_event: Events.SessionAssociatedEvent) {}
  public trackSmsModalDismissed(_event: Events.SmsModalDismissedEvent) {}
  public trackShopperApprovedWidgetClicked(_event: Events.ShopperApprovedWidgetClickedEvent) {}
  public trackThreeDSecureNotSupported(_event: Events.ThreeDSecureNotSupportedEvent) {}
  public trackTopicPageHelpful(_event: Events.TopicPageHelpfulEvent) {}
  public trackTopicPageNotHelpful(_event: Events.TopicPageNotHelpfulEvent) {}
  public trackTopListExpertProfileClicked(_event: Events.TopListExpertProfileClickedEvent) {}
  public trackTopListModuleClicked(_event: Events.TopListModuleClickedEvent) {}
  public trackTopListModuleViewed(_event: Events.TopListModuleViewedEvent) {}
  public trackTopListSellableClicked(_event: Events.TopListSellableClickedEvent) {}
  public trackTopListSocialShareClicked(_event: Events.TopListSocialShareClickedEvent) {}
  public trackTopListViewed(_event: Events.TopListViewedEvent) {}
  public trackUserLoggedIn(_event: Events.UserLoggedInEvent) {}
  public trackUserProfileCollectionModalSubmitted(
    _event: Events.UserProfileCollectionModalSubmittedEvent
  ) {}
  public trackUserProfileCollectionModalTriggered(
    _event: Events.UserProfileCollectionModalTriggeredEvent
  ) {}
  public trackUserRegistered(_event: Events.UserRegisteredEvent) {}
  public trackVideoPlaybackCompleted(_event: Events.VideoPlaybackCompletedEvent) {}
  public trackVideoPlaybackPaused(_event: Events.VideoPlaybackPausedEvent) {}
  public trackVideoPlaybackStarted(_event: Events.VideoPlaybackStartedEvent) {}
  public trackVideoReviewExpanded(_event: Events.VideoReviewExpandedEvent) {}
  public trackVideoReviewCollapsed(_event: Events.VideoReviewCollapsedEvent) {}
  public trackViewCurationLtv(_event: Events.ViewCurationLtvEvent) {}
  public trackViewExpertProfileClicked(_event: Events.ViewExpertProfileClickedEvent) {}
  public trackWarrantyAdded(_event: Events.WarrantyAddedEvent) {}
  public trackWarrantyRemoved(_event: Events.WarrantyRemovedEvent) {}
}
